import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { log } from '@/lib/log';
import { CheckCircle2, Loader2, XCircle, ArrowRight, Sparkles } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';

const MAX_POLL = 10;
const POLL_INTERVAL = 2000;

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const provider = params.get('provider') || 'stripe'; // 'stripe' | 'razorpay'
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking | success | failed | expired
  const [details, setDetails] = useState(null);
  const polledOnceRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    if (polledOnceRef.current) return;
    polledOnceRef.current = true;

    // Razorpay — verify already upgraded the plan server-side; just confirm UI state.
    if (provider === 'razorpay') {
      (async () => {
        try {
          await refresh();
          setStatus('success');
          setDetails({ package_id: 'pro', amount_total: null, currency: 'inr' });
          toast.success('Payment successful — you’re now on Pro 🎉');
        } catch (err) {
          log.warn('razorpay post-verify refresh failed', err);
          setStatus('success'); // still a success — plan was granted server-side
        }
      })();
      return () => {};
    }

    let attempts = 0;
    let timer = null;
    let stopped = false;

    const tick = async () => {
      if (stopped) return;
      try {
        const r = await api.get(`/billing/status/${sessionId}`);
        setDetails(r.data);
        if (r.data.payment_status === 'paid') {
          setStatus('success');
          await refresh();
          toast.success('Payment successful — you’re now on Pro 🎉');
          return;
        }
        if (r.data.status === 'expired') {
          setStatus('expired');
          return;
        }
      } catch (err) {
        // Transient polling errors are expected (rate-limit / network blips).
        log.warn('billing status poll attempt failed', err?.message || err);
      }
      attempts += 1;
      if (attempts >= MAX_POLL) {
        setStatus((s) => (s === 'checking' ? 'failed' : s));
        return;
      }
      timer = setTimeout(tick, POLL_INTERVAL);
    };
    tick();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot polling driven by sessionId only
  }, [sessionId, provider, refresh]);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20">
      <div className="card-base p-8 lg:p-10 relative overflow-hidden text-center">
        <div className="absolute -right-12 -top-16 h-56 w-56 rounded-full bg-[#FF6B1A]/15 blur-3xl pointer-events-none" />
        <div className="absolute -left-10 -bottom-16 h-44 w-44 rounded-full bg-[#39FF14]/10 blur-3xl pointer-events-none" />

        {status === 'checking' && (
          <>
            <div className="h-14 w-14 mx-auto rounded-2xl grid place-items-center bg-gradient-to-br from-[#FF6B1A] to-[#FF8A4A]">
              <Loader2 className="h-7 w-7 text-white animate-spin" />
            </div>
            <h1 className="display text-2xl md:text-3xl font-semibold mt-4">Confirming your payment…</h1>
            <p className="text-white/65 mt-2 text-sm">This usually takes a few seconds. Don’t close this tab.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="h-14 w-14 mx-auto rounded-2xl grid place-items-center bg-gradient-to-br from-[#39FF14] to-[#A8FFCB]" style={{ boxShadow: '0 0 32px rgba(57,255,20,0.45)' }}>
              <CheckCircle2 className="h-7 w-7 text-[#0f1115]" />
            </div>
            <span className="text-[10px] uppercase tracking-widest text-[#39FF14] mt-4 block">// you're in</span>
            <h1 className="display text-2xl md:text-3xl font-semibold mt-2">Welcome to <span className="gradient-text">RevCore Pro</span>.</h1>
            <p className="text-white/65 mt-2 text-sm">Unlimited messages, unlimited agents, streaming on. Now go close something.</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/dashboard" className="btn-primary inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-medium">
                Open Dashboard <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/marketplace" className="btn-secondary inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm">
                <Sparkles className="h-4 w-4" /> Browse Agents
              </Link>
            </div>
            {details && (
              <div className="mt-6 text-[11px] text-white/40 mono">
                {details.package_id} • {details.amount_total ? `$${(details.amount_total / 100).toFixed(2)}` : ''} {details.currency?.toUpperCase()}
              </div>
            )}
          </>
        )}

        {(status === 'failed' || status === 'expired') && (
          <>
            <div className="h-14 w-14 mx-auto rounded-2xl grid place-items-center bg-white/5">
              <XCircle className="h-7 w-7 text-red-400" />
            </div>
            <h1 className="display text-2xl md:text-3xl font-semibold mt-4">{status === 'expired' ? 'Session expired' : "Couldn't confirm payment"}</h1>
            <p className="text-white/65 mt-2 text-sm">Try again or email us if you were charged.</p>
            <a
              href="mailto:info@revcoreai.com?subject=Payment%20issue%20on%20RevCore%20AI"
              className="inline-flex items-center gap-1 text-xs text-white/55 hover:text-[#39FF14] transition-colors mt-2"
            >
              <span className="mono text-[#39FF14]">info@revcoreai.com</span>
            </a>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => navigate('/pricing')} className="btn-primary inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-medium">
                Try again
              </button>
              <Link to="/marketplace" className="btn-secondary inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm">
                Continue exploring
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
